import React, { useEffect, useId, useRef, useState } from "react";
import { Columns3 } from "lucide-react";
import { GlobalButton } from "../forms/GlobalFormFields";
import "./globalDirectory.css";

/**
 * Column visibility popover. `columnDefs`: { id, label }[]. `visibility`: Record<id, boolean>.
 * `onVisibilityChange` receives the next visibility map after each toggle.
 */
export default function GlobalDirectoryColumnPicker({
  columnDefs = [],
  visibility = {},
  onVisibilityChange,
  storageKey = null,
  buttonLabel = "Columns",
  panelTitle = "Show columns",
  panelId: panelIdProp
}) {
  const genId = useId();
  const panelId = panelIdProp || `gds-column-panel-${genId}`;
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const onDoc = (e) => {
      if (!ref.current?.contains(e.target)) setOpen(false);
    };
    if (open) document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const handleToggle = (id) => {
    const next = { ...visibility, [id]: !visibility[id] };
    const visibleCount = columnDefs.filter((c) => next[c.id]).length;
    if (visibleCount === 0) return;
    if (storageKey) {
      try {
        localStorage.setItem(storageKey, JSON.stringify(next));
      } catch {
        /* ignore */
      }
    }
    onVisibilityChange?.(next);
  };

  return (
    <div className="gds-column-picker" ref={ref}>
      <GlobalButton
        type="button"
        size="sm"
        variant="ghost"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        aria-controls={panelId}
        className="gds-directory-toolbar-btn"
      >
        <Columns3 size={16} aria-hidden />
        <span>{buttonLabel}</span>
      </GlobalButton>
      {open ? (
        <div
          id={panelId}
          className="gds-column-picker__panel"
          role="group"
          aria-label={panelTitle}
        >
          <p className="gds-column-picker__title">{panelTitle}</p>
          <ul className="gds-column-picker__list">
            {columnDefs.map((c) => (
              <li key={c.id}>
                <label className="gds-column-picker__row">
                  <input
                    type="checkbox"
                    checked={!!visibility[c.id]}
                    onChange={() => handleToggle(c.id)}
                  />
                  <span>{c.label}</span>
                </label>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  );
}
